import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, RefreshCw, ScrollText, Download, Eye, AlertTriangle, Package } from "lucide-react";

const OP_COLOR = {
  search:   "bg-sky-50 text-sky-700",
  price:    "bg-indigo-50 text-indigo-700",
  book:     "bg-amber-50 text-amber-700",
  ur_modify:"bg-emerald-50 text-emerald-700",
  ticket:   "bg-rose-50 text-rose-700",
  seatmap:  "bg-violet-50 text-violet-700",
  ping:     "bg-slate-100 text-slate-600",
};

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/**
 * AdminSupplierLogs — searchable viewer for the 90-day supplier request/response
 * archive. Every Travelport call (search / price / av-check / book / ur-modify /
 * ticket) is stamped with the user, booking id and timestamp so admin can trace
 * exactly what the host said at each step. Payloads can be downloaded as .xml
 * (single request or response) or .zip (both + metadata).
 */
export default function AdminSupplierLogs() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [supplier, setSupplier] = useState("");
  const [operation, setOperation] = useState("");
  const [onlyFaults, setOnlyFaults] = useState(false);
  const [bookingFilter, setBookingFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [pnrFilter, setPnrFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(null);   // full-log dialog state

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (supplier)      params.set("supplier", supplier);
      if (operation)     params.set("operation", operation);
      if (bookingFilter) params.set("booking_id", bookingFilter);
      if (emailFilter)   params.set("user_email", emailFilter);
      if (pnrFilter)     params.set("pnr", pnrFilter);
      if (dateFrom)      params.set("date_from", dateFrom);
      if (dateTo)        params.set("date_to", dateTo);
      if (onlyFaults)    params.set("only_faults", "true");
      if (q)             params.set("q", q);
      params.set("limit", "300");
      const { data } = await api.get(`/admin/supplier-logs?${params.toString()}`);
      setRows(data.items || []);
      setMeta({ total: data.total, oldest_at: data.oldest_at, retention_days: data.retention_days });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load logs");
    } finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []); // initial

  const download = (logId, part) => {
    const token = localStorage.getItem("fa_admin_token") || "";
    const url = `${backendUrl}/api/admin/supplier-logs/${logId}/download?part=${part}&t=${token.slice(-20)}`;
    // Force-download via a hidden anchor. Token is passed via header on fetch,
    // but the anchor path can't set headers — so we use a temp-fetch then blob.
    fetch(`${backendUrl}/api/admin/supplier-logs/${logId}/download?part=${part}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) { toast.error("Download failed"); return; }
      const blob = await r.blob();
      const a = document.createElement("a");
      const disp = r.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/.exec(disp);
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : `log_${logId.slice(0,8)}.${part === "both" ? "zip" : "xml"}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    });
    void url;
  };

  const openDetail = async (logId) => {
    try {
      const { data } = await api.get(`/admin/supplier-logs/${logId}`);
      setOpen(data);
    } catch { toast.error("Failed to load log details"); }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  const counts = useMemo(() => ({
    faults: rows.filter((r) => r.has_fault).length,
    ok: rows.filter((r) => !r.has_fault).length,
  }), [rows]);

  return (
    <div className="space-y-4 p-6" data-testid="admin-supplier-logs">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)] flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-[var(--brand-primary)]" /> Supplier Logs
          </h1>
          <p className="text-slate-500 mt-1 max-w-3xl text-sm leading-relaxed">
            Every Travelport / Amadeus request &amp; response ({meta.retention_days || 90}-day retention) — searchable by user, booking, supplier and operation. Download any payload as raw XML or a full booking archive as ZIP.
          </p>
        </div>
        <button onClick={load} disabled={busy} data-testid="logs-refresh-btn"
                className="rounded-full px-3 py-2 text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 flex items-center gap-1.5">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="logs-stats">
        <Card className="p-3"><div className="text-xs font-bold uppercase text-slate-500">Total (archive)</div><div className="font-heading text-2xl font-bold text-[var(--brand-accent)]" data-testid="stat-total">{meta.total ?? 0}</div></Card>
        <Card className="p-3"><div className="text-xs font-bold uppercase text-slate-500">Loaded now</div><div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">{rows.length}</div></Card>
        <Card className="p-3"><div className="text-xs font-bold uppercase text-emerald-700">Clean</div><div className="font-heading text-2xl font-bold text-emerald-700">{counts.ok}</div></Card>
        <Card className="p-3"><div className="text-xs font-bold uppercase text-rose-700">Faults</div><div className="font-heading text-2xl font-bold text-rose-700">{counts.faults}</div></Card>
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="relative md:col-span-2">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search payload / booking / user" className="pl-9" data-testid="logs-search"/>
          </div>
          <Select value={supplier || "all"} onValueChange={(v)=>setSupplier(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="logs-supplier-filter"><SelectValue placeholder="Supplier"/></SelectTrigger>
            <SelectContent><SelectItem value="all">All suppliers</SelectItem><SelectItem value="travelport">Travelport</SelectItem><SelectItem value="amadeus">Amadeus</SelectItem></SelectContent>
          </Select>
          <Select value={operation || "all"} onValueChange={(v)=>setOperation(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="logs-op-filter"><SelectValue placeholder="Operation"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ops</SelectItem>
              <SelectItem value="search">search</SelectItem>
              <SelectItem value="price">price</SelectItem>
              <SelectItem value="book">book</SelectItem>
              <SelectItem value="ur_modify">ur_modify</SelectItem>
              <SelectItem value="ticket">ticket</SelectItem>
              <SelectItem value="seatmap">seatmap</SelectItem>
            </SelectContent>
          </Select>
          <Input value={bookingFilter} onChange={(e)=>setBookingFilter(e.target.value)} placeholder="Booking id" data-testid="logs-booking-filter"/>
          <Input value={emailFilter} onChange={(e)=>setEmailFilter(e.target.value)} placeholder="User email" data-testid="logs-email-filter"/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-2">
          <Input value={pnrFilter} onChange={(e)=>setPnrFilter(e.target.value.toUpperCase())} placeholder="PNR (e.g. ABC12D)" data-testid="logs-pnr-filter" className="uppercase md:col-span-2"/>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Date from</label>
            <Input type="datetime-local" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} data-testid="logs-date-from"/>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Date to</label>
            <Input type="datetime-local" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} data-testid="logs-date-to"/>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-4">
            <label className="text-xs flex items-center gap-1.5 text-slate-600">
              <input type="checkbox" checked={onlyFaults} onChange={(e)=>setOnlyFaults(e.target.checked)} data-testid="logs-only-faults"/>
              <AlertTriangle className="w-3 h-3 text-rose-500"/> Only host faults
            </label>
            <button
              onClick={() => { setQ(""); setSupplier(""); setOperation(""); setBookingFilter(""); setEmailFilter(""); setPnrFilter(""); setDateFrom(""); setDateTo(""); setOnlyFaults(false); setTimeout(load, 0); }}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
              data-testid="logs-reset-btn">Reset filters</button>
          </div>
          <button onClick={load} className="rounded-full px-4 py-1.5 text-xs font-semibold bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="logs-apply-btn">Apply</button>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <Table className="min-w-[1000px]">
          <TableHeader><TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Op</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Booking</TableHead>
            <TableHead className="text-right">HTTP</TableHead>
            <TableHead className="text-right">Duration</TableHead>
            <TableHead className="text-right">Size (req/res)</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`log-row-${r.id}`} className={r.has_fault ? "bg-rose-50/40" : ""}>
                <TableCell className="text-xs text-slate-600 whitespace-nowrap">{fmt(r.created_at)}</TableCell>
                <TableCell><Badge className="border-0 bg-slate-100 text-slate-700 text-[10px]">{r.supplier}</Badge></TableCell>
                <TableCell><Badge className={`${OP_COLOR[r.operation] || "bg-slate-100"} border-0 text-[10px]`}>{r.operation}</Badge></TableCell>
                <TableCell className="text-xs">{r.user_email || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-xs font-mono">{r.booking_id ? r.booking_id.slice(0, 8) : "—"}</TableCell>
                <TableCell className="text-right text-xs">{r.http_status}{r.has_fault && <span className="ml-1 text-rose-600 font-bold">!</span>}</TableCell>
                <TableCell className="text-right text-xs">{r.duration_ms}ms</TableCell>
                <TableCell className="text-right text-xs text-slate-500">{(r.request_size/1024).toFixed(1)}k / {(r.response_size/1024).toFixed(1)}k</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openDetail(r.id)} title="View" className="text-slate-600 hover:text-[var(--brand-primary)] p-1 rounded" data-testid={`log-view-${r.id}`}><Eye className="w-3.5 h-3.5"/></button>
                    <button onClick={() => download(r.id, "request")} title="Download request" className="text-slate-600 hover:text-[var(--brand-primary)] p-1 rounded" data-testid={`log-dl-req-${r.id}`}><Download className="w-3.5 h-3.5"/><span className="text-[9px] font-bold ml-0.5">REQ</span></button>
                    <button onClick={() => download(r.id, "response")} title="Download response" className="text-slate-600 hover:text-[var(--brand-primary)] p-1 rounded" data-testid={`log-dl-res-${r.id}`}><Download className="w-3.5 h-3.5"/><span className="text-[9px] font-bold ml-0.5">RES</span></button>
                    <button onClick={() => download(r.id, "both")} title="Download both (.zip)" className="text-slate-600 hover:text-[var(--brand-primary)] p-1 rounded" data-testid={`log-dl-both-${r.id}`}><Package className="w-3.5 h-3.5"/></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10">No supplier logs match the filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!open} onOpenChange={(v)=>!v && setOpen(null)}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="log-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-[var(--brand-primary)]"/>
              {open?.supplier} · {open?.operation} · {fmt(open?.created_at)}
            </DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><div className="text-slate-400">User</div><div className="font-mono">{open.user_email || "—"}</div></div>
                <div><div className="text-slate-400">Booking</div><div className="font-mono">{open.booking_id || "—"}</div></div>
                <div><div className="text-slate-400">HTTP</div><div className="font-mono">{open.http_status} {open.has_fault ? <span className="text-rose-600">Fault</span> : ""}</div></div>
                <div><div className="text-slate-400">Duration</div><div className="font-mono">{open.duration_ms}ms</div></div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={()=>download(open.id, "request")} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 flex items-center gap-1.5"><Download className="w-3 h-3"/> request.xml</button>
                <button onClick={()=>download(open.id, "response")} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 flex items-center gap-1.5"><Download className="w-3 h-3"/> response.xml</button>
                <button onClick={()=>download(open.id, "both")} className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)] flex items-center gap-1.5"><Package className="w-3 h-3"/> both (.zip)</button>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Request</div>
                <pre className="text-[11px] bg-slate-900 text-emerald-100 p-3 rounded-lg overflow-x-auto max-h-72" data-testid="log-request-body">{open.request || ""}</pre>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Response</div>
                <pre className="text-[11px] bg-slate-900 text-sky-100 p-3 rounded-lg overflow-x-auto max-h-72" data-testid="log-response-body">{open.response || ""}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
